/**
 * The policy layer's vocabulary (`docs/design/00-contracts.md` §11.2–§11.3, C-19, C-32).
 *
 * Principle 2: deny by default, and **one** function decides authorization. Everything in this
 * file exists so that `can()` can be pure, synchronous and free of I/O — which is what lets the
 * browser call the very same function the API enforces with (principle 7).
 *
 * The role → action bit is **data**, not code (decision 0001): it lives in `role_permissions`
 * per organization and reaches the policy layer as a `PermissionSet` on the `Actor`. What stays
 * in code is which actions exist at all, how a permitted `list` is narrowed, the record-level
 * conditions (`rules.ts`), and the handful of actions that can never be granted away
 * (`default-permissions.ts`).
 */
import { z } from 'zod';

import { ORG_ROLES } from '../enums.js';
import type {
  ActorRole,
  EditionRole,
  GrantPermission,
  OrgRole,
  OrganizationKind,
} from '../enums.js';
import type { EditionId, OrganizationId, PersonId, UserId, Uuid } from '../ids.js';
import type { OrganizationModule } from '../contracts/control-room-modules.js';
import { LOCKED_ACTIONS } from './default-permissions.js';
import type { AiAccessSwitches, ExternalClient } from './external-access.js';

export type { ActorRole, EditionRole, GrantPermission, OrgRole } from '../enums.js';

/**
 * Mirrors `object_type`, plus the tables that are not shareable objects. Every value is
 * `snake_case`, matching `role_permissions.resource` and `audit_log.entity_type`'s subject.
 */
export const RESOURCE_TYPES = [
  'organization',
  'festival',
  'edition',
  'person',
  'contact',
  'task',
  'thread',
  'message',
  'department',
  'document',
  'performance',
  /*
   * NOT A TABLE, AND THE ONLY ONE HERE THAT IS NOT (sak 53).
   *
   * Every other value mirrors a row somebody can point at. `economy` is a VIEW of rows that live
   * elsewhere — cost lines on performances, staffing costs on people — and it exists as a resource
   * because Atle asked for the door to be a setting rather than a hardcoded route: "Ha dette som et
   * option i innstillinger på rettigheter. Slike ting skal helst ikke hardkodes."
   *
   * It is therefore never a RECORD. `economy:read` is asked about the collection, and what it
   * answers is "may this role open the money". The rows behind it keep their own cells.
   *
   * Not in `object_type` either: a budget is not shared with an external the way one booking is.
   */
  'economy',
  // The festival's shape, read by everybody who can see the festival (29 August, A = 3). In
  // `object_type` since F14 (0640): its running order is shared as a LINK, never with a login.
  'stage',
  'accreditation_level',
  'crew_member',
  'shift',
  /*
   * KALENDEREN, SOM SIN EGEN CELLE (rettelse B12, Atles avgjørelse 17. september).
   *
   * Hver kalenderrute spurte om `task:list` før denne, og det lånet lakk begge veier: en
   * organisasjon som skrudde AV oppgaver for en rolle stengte kalenderen deres samtidig, og en
   * som ville skjule kalenderen måtte ta fra dem oppgavene. Kalenderen tegner dessuten møter,
   * skift, tildelinger og programposter — ikke bare oppgaver.
   *
   * IKKE I `object_type`: en hendelse deles ikke med en ekstern slik en booking gjør. Derfor er
   * `external` `no` og ikke `grant` i malen.
   */
  'event',
  /*
   * FRIVILLIGE (PEOPLE.md, fane 2), and TWO resources rather than one.
   *
   * A signup is a person's data — name, phone, answers, consent — and a form is an outward-facing
   * page anybody with the link can write to. Who may read a volunteer list and who may publish a
   * public URL under the festival's name are different powers held by different people, and one
   * cell could not say so: `volunteer:update` on a form would mean "may edit a person's answers"
   * and "may reopen a closed signup page" at once.
   *
   * Neither is in `object_type`: an external is granted a booking, never a recruitment.
   */
  'volunteer',
  'signup_form',
  /*
   * AVTALEN MELLOM BYRÅET OG ARTISTEN (ARTIST-AVTALE-ORDREN Del 1).
   *
   * EN EGEN CELLE OG IKKE `performance:read`, som resten av byråets artistside låner. Det lånet er
   * riktig for stamdata og bookinger — de HANDLER om programposter — men avtalen er noe annet: den
   * bærer provisjonssatsen, minstehonoraret og hvem som fakturerer arrangøren. Et byrå kan godt
   * ville at hele huset ser bookingene og bare to mennesker ser hva huset tjener på dem.
   *
   * IKKE I `object_type`, og det er hele poenget: en avtale deles ALDRI. Ikke med festivalen, ikke
   * med en ekstern, ikke gjennom en `object_grant`. Den har ingen `grant`-celle å skru på, slik
   * Kontrakt › Økonomi ikke har det — det er forholdet mellom to andre parter enn den som leser.
   */
  'artist_agreement',
  /* Artisten i byråets stall (sak 216.14, 215.29): å invitere mennesket i bandet, og å arkivere artisten. */
  'artist',
  /*
   * MASSEUTSENDING (F34, runde 4. oktober): beskjed og vaktplan på e-post til mange på én gang.
   * `broadcast:create` sender, `broadcast:list` ser hva som er sendt. Ikke i `object_type`.
   */
  'broadcast',
  /*
   * AKKREDITERING (PEOPLE.md, fane 3), and its own resource rather than a borrowed one.
   *
   * `accreditation_level` above is the Settings list — «hvilke soner gir dette kortet tilgang
   * til» — and is edited once a year by an admin. THIS is the desk: who has been handed a card,
   * who is still missing a level, and the press pass somebody adds at 16:00 on the Thursday. The
   * two are different powers held by different people, which is exactly what one cell cannot say.
   *
   * Not in `object_type`: a badge is never shared with an external party.
   */
  'accreditation',
  /*
   * The SHELF, separate from what stands on it (Documents [F2]).
   *
   * `document` already existed and answers "may this role open this file". Restructuring the
   * folders is a different power held by different people: everybody in production uploads a
   * signed contract, and almost nobody should be able to delete the folder it lives in — a
   * distinction one resource cannot express, since `document:delete` would then mean both
   * "remove a file" and "remove a hundred files at once".
   *
   * Not in `object_type`: a folder is not shared with an external. The FILE is, and `document` is
   * in that enum already.
   */
  'folder',
  /*
   * KONTRAKTFANEN PÅ EN PROGRAMPOST (PERFORMANCE.md: «Hele fanen krever egen rettighet og deles
   * aldri med eksterne»).
   *
   * Its own resource rather than a borrowed `performance` cell, and that is the whole point of the
   * sentence: everything else on that page is shared with a tour manager one section at a time, and
   * this one part never is. A tab gated on `performance:read` would open for every external who
   * was granted the booking — which is exactly the group it must not open for.
   *
   * The spec writes the cell as `contract:view`; the verb set has `read`, and a ninth synonym for
   * reading would be a worse trade than the rename.
   *
   * NOT IN `object_type`: a contract is never shared on its own terms. That is the point.
   */
  'contract',
  /*
   * RESSURSLAGET (trinn 1): typen, tingen og tidsrommet.
   *
   * TRE RESSURSER OG IKKE ÉN, fordi de svarer på tre ulike spørsmål om hvem som får gjøre hva. Å
   * definere en TYPE er å bestemme hva festivalen i det hele tatt holder styr på — det er
   * Innstillinger, og det er admin. Å legge inn en RESSURS er registerarbeid. Å lage en TILDELING
   * er daglig produksjon: den som klokka 14 oppdager at bussen må hente en time tidligere skal
   * kunne flytte den selv, uten å be om noe.
   *
   * INGEN AV DEM STÅR I `object_type`: en ressurs deles ikke på sine egne premisser. Byrået ser
   * navn og tid gjennom den delte PROGRAMPOSTEN (spesifikasjonen §6), og det er en annen
   * mekanisme — `visible_to_agency` pluss RLS — som kommer i trinn 2.
   */
  'resource_type',
  'resource',
  'allocation',

  /*
   * DELTAKELSESLAGET (trinn 3). Samme tredeling som over, og av den samme grunnen: å definere et
   * FELT eller en ROLLE er Innstillinger, og det er admin. Å føre et menneske inn i årgangen er
   * registerarbeid, og en member gjør det hver dag.
   *
   * INGEN AV DEM STÅR I `object_type` heller. En deltakelse deles ikke på sine egne premisser:
   * byråets folk står i festivalens liste fordi PROGRAMPOSTEN er delt, og identiteten eies gjennom
   * `identity_locked` — som er eierskap og ikke en policycelle. Det er hele grunnen til at et
   * forsøk svares med 409 framfor 403 (Atles avgjørelse 4).
   */
  'person_field',
  'participation_role',
  'participation',
  'membership',
  /* Tittel, ansvarsområde og «rapporterer til» — se default-permissions (QA12 #7). */
  'member_profile',
  /* Det samme om en SUPERADMIN, for en som ikke selv er superadmin (sak 239.36). */
  'superadmin_profile',
  /* «Organisasjoner vi samarbeider med»: å legge til og å svare (sak 239.21). */
  'organization_partner',
  'invitation',
  'tag',
  'notification',
  /*
   * UTKAST (AI-ANALYSE D9, 279.10 a): en melding eller et bud som er skrevet, men ikke sendt. Bare
   * forfatteren ser det (`own` for alle roller, og RLS sier det samme). `draft:send` er sin egen
   * celle fordi en AI får lage utkastet, men aldri sende det (`external-access.ts`).
   */
  'draft',
  'object_grant',
  'audit_log',
  'user',
  'consent',
  'role_permission',
  // Spillesteder og konserter (27. september, docs/SPILLESTED-KONSERT-PLAN.md).
  'venue',
  'venue_rate',
  'venue_enquiry',
  'venue_night',
  'venue_registry',
  'date_request',
  'concert',
  'concert_budget',
  'concert_template',
  // «Overganger» på Booking (28. september): byråbyttene skanningen og byråene selv melder.
  'agency_move',
  /* F21 (pakke 3): hvem som får varsel om bud og forespørsler ingen agent er ansvarlig for. Ikke en tabell. */
  'booking_enquiry',
  /*
   * DELINGENE (sak 232, 0459). `external:invite` er å invitere en ekstern (personlig konto),
   * `share_link:create` å lage en lenke til en lenkeperson, `share:edit_level` å dele med nivået
   * «kan endre». Ingen av dem er en tabell; de er cellene Atle godkjente 29.09.
   */
  /*
   * EKSPORT (docs/EKSPORT.md §6): «Hvem som kan eksportere, er en celle i Rettigheter.» `export:create`
   * er cella; HVA som kommer med, avgjøres av de samme cellene og den samme RLS-en som skjermen.
   * Tabellen er `export_jobs` (0907), men ressursen er handlingen, ikke raden. Ikke i `object_type`.
   */
  'export',
  'external',
  'share_link',
  'share',
  /*
   * ADVANCER ANDRE ORGANISASJONER HAR DELT (239.37). `shared_advance:read` avgjør om rollen ser en
   * delt advance fra en annen organisasjon; Begrenset gjør det ikke som standard. Ikke en tabell.
   */
  'shared_advance',
  /*
   * ÅRGANGENS KART OG OMRÅDE SOM NOE SOM DELES (F13, 0661). `map:share` er cella; selve kartet leses
   * og endres fortsatt med `edition:read`/`edition:update`. Ikke en tabell — kartet er kolonner på
   * årgangen og punktene i `map_points`.
   */
  'map',
  /*
   * BOOKING OG ARTISTER FÅR EGNE RETTIGHETER (G2, QA13 #87, 0923).
   *
   * Til 0923 styrte «Lage og endre programmet» under Festival hele bookingregisteret og hele
   * byråets stall (`performance:*` på rundt 130 ruter). For et rent byrå var Festival-seksjonen
   * skjult, så bryteren som styrte alt byrået gjorde, sto ikke på skjermen. Startverdien til hver
   * ny celle er kopiert fra `performance:*` per organisasjon og rolle, så ingenting endret seg før
   * noen flyttet en bryter. Ingen av dem er tabeller; de er handlingene i Booking og Artister.
   *
   *   booking          se registeret (`list`), og slette utkast, bud og priser (`delete`)
   *   booking_artist   legge inn, endre og slette eller skjule artister i registeret
   *   booking_draft    programmere: utkast og plasseringer
   *   booking_bid      gi, sende og trekke bud (`create`/`update`), og svare på motbud (`respond`)
   *   booking_amount   se priser og honorar i booking (før: `contract:read` ELLER `performance:update`)
   *   agency_booking   byråets egne jobber (`create`/`update`), og å svare på forespørsler og bud
   */
  'booking',
  'booking_artist',
  'booking_draft',
  'booking_bid',
  'booking_amount',
  'agency_booking',
] as const;
export type ResourceType = (typeof RESOURCE_TYPES)[number];

/** Closed verb set. A new verb is a decision, not an ad-hoc string. */
export const VERBS = [
  'list',
  'read',
  'create',
  'update',
  'delete',
  'restore',
  'archive',
  'assign',
  'complete',
  /**
   * Saying something about a record without changing it (sak 56, answered 31 August).
   *
   * A verb of its own rather than a reading of `update`, because Atle's answer was «slik hører
   * hjemme i innstillinger»: whether somebody may join the conversation on work they can see is an
   * organisation's call, and a cell can only be switched if it exists. It also settles the case
   * that forced the question — an `external` with a view grant who needs to answer a question about
   * their own show, without being able to move the deadline.
   */
  'comment',
  'invite',
  'manage_roles',
  /**
   * «Rollen får alt av denne typen, også det som lages i morgen» (sak 182, 21. september).
   *
   * Et verb og ikke en celleverdi, fordi `role_permissions` bare lagrer `allowed` — se den lange
   * begrunnelsen ved `festival:access_all` i `default-permissions.ts`. Spørsmålet Atle stilte
   * («hvilke roller får automatisk tilgang til alle festivaler og venues?») er boolsk per rolle, og
   * det er nøyaktig formen den tabellen har.
   *
   * Det er hakket UNDER `list` og `read`: de sier om du ser lista i det hele tatt, dette sier hvor
   * mange rader som står i den. En rolle uten `access_all` ser fortsatt festivalsida — bare de
   * festivalene hen er lagt til i.
   */
  'access_all',
  'share',
  'export',
  'merge',
  /**
   * Å SVARE PÅ NOE SOM ER GITT DEG (201.14): «Bekreft» på en vakt, og «Takk nei» når årgangen
   * tillater det. Eget verb fordi det ikke er en endring av raden — vaktplanen er lederens, svaret
   * er personens — og en frivillig uten `shift:update` skal kunne gi det.
   */
  'respond',
  /** Å dele med nivået «kan endre» (`share:edit_level`, sak 232). */
  'edit_level',
  /** Å FÅ ET VARSEL når ingen er navngitt (`booking_enquiry:notify`, F21). Ikke en handling på raden. */
  'notify',
  /** Kontrakten (sak 274): lage og fylle ut utkastet (`contract:draft`). */
  'draft',
  /** Annullere en gjeldende kontrakt (`contract:void`, C7). */
  'void',
  /** Registrere en kontrakt signert utenfor Tvitvi (`contract:register`, C4.4). Atskilt fra fullmakten. */
  'register',
  /** Sende et utkast (`draft:send`, D9): selve sendingen, atskilt fra å lage utkastet. */
  'send',
  /**
   * Å BE OM NOE FOR SEG SELV (`shift:request`, QA16 297.4): en ledig vakt, eller å bytte vakt med en
   * kollega. Ikke en endring av vakta — den gjør den som godkjenner (`shift:assign`).
   */
  'request',
  /**
   * Å VISE FRAM (`venue:publish`, QA16 301.10): salens synlighet og den publiserte kalenderen. Egen celle,
   * så den som endrer salen ikke uten videre bestemmer hvem som ser den.
   */
  'publish',
] as const;
export type Verb = (typeof VERBS)[number];

/** `<resource_type>:<verb>`, both snake_case. Nothing else type-checks. */
export type Action = `${ResourceType}:${Verb}`;

const RESOURCE_TYPE_SET: ReadonlySet<string> = new Set(RESOURCE_TYPES);
const VERB_SET: ReadonlySet<string> = new Set(VERBS);

/** Splits an `Action` into its two halves — the pair `role_permissions` stores in two columns. */
export function splitAction(action: Action): { resource: ResourceType; verb: Verb } {
  const index = action.indexOf(':');
  return {
    resource: action.slice(0, index) as ResourceType,
    verb: action.slice(index + 1) as Verb,
  };
}

/** Joins the two `role_permissions` columns back into an `Action`. */
export function joinAction(resource: ResourceType, verb: Verb): Action {
  return `${resource}:${verb}`;
}

/**
 * Shape check against the catalogues above: `<known resource>:<known verb>`. Whether an action
 * additionally has a `Rule` — and is therefore reachable at all — is `isKnownAction()` in
 * `rules.ts`; this one guards the wire, where a value must at minimum be a well-formed member of
 * the two closed sets before anything else looks at it.
 */
export function isAction(value: unknown): value is Action {
  if (typeof value !== 'string') return false;
  const index = value.indexOf(':');
  if (index <= 0) return false;
  return RESOURCE_TYPE_SET.has(value.slice(0, index)) && VERB_SET.has(value.slice(index + 1));
}

/** Validates against the known action catalogue; used by the DTOs that carry actions on the wire. */
export const actionSchema: z.ZodType<Action> = z
  .custom<Action>(isAction, { message: 'Not a known <resource>:<verb> action.' })
  .meta({ id: 'Action' });

export interface ObjectGrantRef {
  readonly objectType: ResourceType;
  readonly objectId: Uuid;
  readonly permission: GrantPermission;
  /** ISO-8601 string, or null for "no expiry". Never a Date: this crosses the wire. */
  readonly expiresAt: string | null;
  /**
   * HVILKE DELER AV OBJEKTET DELINGEN DEKKER — null er hele det.
   *
   * Skrevet siden fane 8 og aldri lest før sak 72 (Atle, 2. september): «Legg dette som et valg
   * når man legger til en kontakt i acten. Både for chat, men også kontrakt og økonomi.» Så lenge
   * kontrakten var plattformlåst og alt annet var alt-eller-ingenting kunne `can()` klare seg uten
   * den; i det en booking kan deles UTEN kontrakten er den forskjellen på et grant som virker og
   * et som lyver.
   *
   * En TOM liste er ikke det samme som null. Kontrakten refuserer den — «et grant som viser
   * ingenting er en tilbakekalling med et annet navn» — så den kan ikke oppstå.
   */
  readonly scopes: readonly string[] | null;
}

/**
 * EN DELING GJESTEN HAR (sak 232, PLAN 1.1). `kind` er alltid `external` på en innlogget gjest —
 * en lenkedeling gir aldri innlogging — men står her så kontrakten sier det samme som raden.
 */
export type GuestGrant = ObjectGrantRef & {
  readonly id: Uuid;
  readonly kind: 'external' | 'link';
  readonly contactPersonId: Uuid | null;
  /**
   * EN PROGRAMPOST EN ÅRGANGSDELING IKKE TAR MED (246.3, 0990): ikke bekreftet, eller avlyst (242.2).
   * Da er den direkte delingen den eneste veien inn, og omfanget dens gjelder — også når årgangen
   * er delt. Utelatt: en bekreftet post, eller noe annet enn en programpost.
   */
  readonly beyondEdition?: boolean;
};

/**
 * ALT EN GJEST NÅR, lest av `app.resolve_guest_actor` (0458). `departments` er allerede utvidet til
 * underavdelinger og til alle avdelinger i en delt årgang, med det høyeste nivået per avdeling.
 * `editions` er årganger som er delt DIREKTE (hele årgangen).
 */
export interface GuestScope {
  readonly grants: readonly GuestGrant[];
  readonly departments: ReadonlyMap<Uuid, GrantPermission>;
  readonly editions: ReadonlyMap<Uuid, GrantPermission>;
  /**
   * TRÅDENE GJESTEN NÅR, med nivået per tråd (0469, `app.guest_thread_levels`): delingstråden, og
   * chatten på en delt programpost eller oppgave — aldri en intern. Den samme lista basen leser
   * (`guest_thread_ids`), så `can()` og RLS svarer likt. Valgfri fordi mange tester bygger en
   * gjest for hånd; uten feltet når gjesten ingen tråd.
   */
  readonly threads?: ReadonlyMap<Uuid, GrantPermission>;
  /**
   * DET EN DELT SCENE NÅR (§5.3, 1102, `guestReach` fra `app.resolve_guest_actor`), nøklet
   * `<type>:<id>`: programpostene og ressursen på «se», reservasjonene på scenen, og vaktene på
   * delingens nivå. Den samme lista basen leser (`guest_stage_*`). Valgfri som `threads`.
   */
  readonly reach?: ReadonlyMap<string, GrantPermission>;
  /**
   * DEN TEKNISKE ADVANCEN PÅ PROGRAMPOSTENE SCENEN NÅR (§5.3): «Kan endre» på scenen gir å endre den,
   * og bare den — posten selv er «se». Nøkkelen er programpostens id.
   */
  readonly stageTechnical?: ReadonlyMap<Uuid, GrantPermission>;
}

/** Everything the policy layer is allowed to know about who is asking. */
export interface Actor {
  readonly userId: UserId;
  readonly personId: PersonId;
  readonly organizationId: OrganizationId;
  /**
   * HVA SLAGS HUS DETTE ER — `festival` eller `agency` (migrering 0081, QA14 #3a).
   *
   * -------------------------------------------------------------------------------------------
   * DEN ER HER FORDI EN REGEL BETINGER PÅ DEN, og `can()` kan ikke slå den opp: funksjonen er ren
   * og synkron, uten I/O, nettopp for at nettleseren skal kunne stille det samme spørsmålet som
   * API-et svarer på (prinsipp 7). Alt en regel trenger å vite om den innloggede må derfor ligge
   * her, og aktøren bygges ett sted — `app.resolve_actor` (0347) på serveren, `/v1/me`s
   * medlemskap i nettleseren.
   *
   * Atle, QA14 #3a: «Provisjon er et forhold mellom artist og byrå.» Fram til da var provisjonen
   * gjemt i en meny og åpen i ruten, DTO-en og endepunktet. En avgjørelse som bare finnes i en
   * meny er ikke en tilgangsregel; den er en vane.
   */
  readonly organizationKind: OrganizationKind;
  /**
   * MODULENE ORGANISASJONEN HAR (0483; sak 239.40). Provisjon og avtaler følger modulen Byrå, ikke
   * typen: en festival med modulen Byrå har begge. Valgfri, fordi en aktør kan bygges uten dem (en
   * gjest, kalenderstrømmen, testene) — da slipper modulsteget i `can()` gjennom, og «er dette et
   * byrå?» svares av `agencyModule`.
   */
  readonly modules?: ReadonlySet<OrganizationModule>;
  /**
   * ER MODULEN ARTISTER (BYRÅ) PÅ? (0973, 246.6.) `app.resolve_actor` bærer den også der modulene
   * mangler, så `hasAgencyModule` aldri trenger typen. Uten noen av dem er svaret nei.
   */
  readonly agencyModule?: boolean;
  /**
   * Medlemsrollen, eller `guest` (sak 232): ikke medlem, bare levende delinger i huset. En gjest
   * leser aldri matrisen — `can()` svarer fra `policy/guest.ts`.
   */
  readonly orgRole: ActorRole;
  /** editionId -> role. Empty until Phase 2. */
  readonly editionRoles: ReadonlyMap<EditionId, EditionRole>;
  /**
   * FESTIVALENE PERSONEN ER LAGT TIL I — ALLE ÅRGANGER, OGSÅ DE SOM IKKE FINNES ENNÅ (sak 182).
   *
   * `edition_roles` svarer «denne ene årgangen»; dette svarer «festivalen, framover». Atle ba om
   * begge som to valg på den samme avkryssinga: «Bare denne årgangen» / «Alle årganger av
   * festivalen (tidligere og framtidige)».
   *
   * Den andre KAN ikke være rader i `edition_roles`: én rad per årgang dekker de årgangene som
   * finnes i dag og ingen av dem som lages i morgen, og da faller personen stille ut av festivalen
   * sin ved neste årgang uten at noen har gjort noe. Derfor peker raden på festivalen.
   *
   * Tom for alle som har `festival:access_all` — de ser alt uansett, og da er lista uinteressant.
   */
  readonly festivalAccess: ReadonlySet<Uuid>;
  readonly departmentIds: ReadonlySet<Uuid>;
  readonly grants: readonly ObjectGrantRef[];
  /** The organization's permission matrix, loaded once per request and answered from memory. */
  readonly permissions: PermissionSet;
  /**
   * Gjestens delinger (sak 232). `null`/fraværende for et medlem. Valgfri og ikke påkrevd, slik at
   * aktørene nettleseren og testene bygger slipper å bære et felt som bare en gjest har; en gjest
   * uten det får ingenting på en post (`guestCan` nekter).
   */
  readonly guest?: GuestScope | null;
  /**
   * KALLET KOM MED ET TOKEN UTENFRA (B3): en AI eller en integrasjon på vegne av brukeren. Da spør
   * `can()` også organisasjonens AI-brytere og sperrelista (`external-access.ts`), også for superadmin.
   * Fraværende for nettsiden og mobilappen.
   */
  readonly external?: ExternalClient;
  /** Organisasjonens AI-brytere, lest ved hvert kall. Bare satt sammen med `external`. */
  readonly aiAccess?: AiAccessSwitches;
}

export interface CollectionResource {
  readonly kind: 'collection';
  readonly type: ResourceType;
  readonly organizationId: OrganizationId;
  readonly editionId?: EditionId;
  /** Spurt inne i én bestemt modul (G1): se `moduleGate` i `modules.ts`. */
  readonly inModule?: OrganizationModule;
}

/** A single record. The optional fields are the facts rules may branch on. */
export interface RecordResource {
  readonly kind: 'record';
  readonly type: ResourceType;
  readonly id: Uuid;
  readonly organizationId: OrganizationId;
  readonly editionId?: EditionId | null;
  /**
   * Festivalen raden hører til (sak 182). Settes av resolveren, aldri av klienten.
   *
   * Finnes ved siden av `editionId` fordi tilgang kan være gitt på festivalnivå — «alle årganger,
   * også framtidige» — og da er årgangens egen id ikke nok til å svare.
   */
  readonly festivalId?: Uuid | null;
  readonly createdByPersonId?: PersonId | null;
  readonly assigneePersonId?: PersonId | null;
  readonly departmentId?: Uuid | null;
  /**
   * AVDELINGEN OG DEN OVER DEN (0357, 201.15). Settes av resolveren, som vet treet: en
   * avdelingsleder for «Område» styrer også «Område › Inngang». `departmentId` alene er raden; dette
   * er hvem som svarer for den.
   */
  readonly departmentIds?: readonly Uuid[];
  readonly participantPersonIds?: readonly PersonId[];
  /**
   * DENNE RADEN LIGGER I ET ANNET HUS, OG DE TO HAR SAGT JA TIL Å DELE DEN (ARTISTS.md §4.1).
   *
   * ---------------------------------------------------------------------------------------------
   * Atle, 11. september: «Vi ønsker jo at det skal være samme objekt slikt at vi slipper å gjøre
   * endringer to ganger.» En advance er én programpost to organisasjoner fører sammen — byrået
   * legger inn rideren, festivalen rommene, og ingen av dem gjør det to steder.
   *
   * Uten dette feltet stopper steg 1 dem med `wrong_organization`. Med det åpnes nøyaktig de
   * handlingene delingen dekker, og ingen andre: å slette motpartens programpost, eller å lese
   * kontrakten med økonomien i, er fortsatt utenfor.
   *
   * SETTES BARE AV EN RESOLVER SOM HAR SPURT BASEN — `app.in_shared_performance` leser
   * `shared_advances`, altså raden begge hus faktisk har vært inne i. Klienten kan ikke påstå den;
   * feltet finnes ikke i noen forespørsel.
   */
  readonly sharedWithActor?: boolean;
  /**
   * ADVANCE-SEKSJONEN EN SKRIVING GJELDER (§5.3): `template_key` på seksjonen (`technical`, `promo`,
   * `hospitality`, eller null). Settes av advancens resolver; en delt scene gir «endre» bare på
   * `technical`.
   */
  readonly advanceSection?: string | null;
  /** For membership and invitation changes: the role being granted or revoked. */
  readonly targetRole?: OrgRole;
  /**
   * For a membership that EXISTS: the role it has now. The escalation rule is about both ends —
   * an admin may neither give nor TAKE AWAY a role at or above their own (QA runde 5, H8).
   */
  readonly currentRole?: OrgRole;
  /**
   * For a membership update that does not touch the role at all (title, responsibility, reports-to).
   * Then there is nothing to escalate, and the rank check does not apply (QA runde 5, M7).
   */
  readonly roleUnchanged?: boolean;
  /** For self-scoped rows: the person the row belongs to. */
  readonly subjectPersonId?: PersonId | null;
  /**
   * For en celle i Rettigheter (`role_permission:update` om én rad, sak 239.38): handlingen cella
   * gjelder. Rollen er `targetRole`. Bare superadmin endrer cellene for Rettigheter selv.
   */
  readonly permissionAction?: Action;
  /** Spurt inne i én bestemt modul (G1): se `moduleGate` i `modules.ts`. */
  readonly inModule?: OrganizationModule;
}

export type Resource = CollectionResource | RecordResource;

/** How a permitted `list` must be narrowed. Repositories MUST honour this. */
export type QueryScope =
  | { readonly kind: 'all' }
  | { readonly kind: 'own'; readonly personId: PersonId }
  | { readonly kind: 'departments'; readonly departmentIds: readonly Uuid[] }
  /**
   * MINE RADER, OG RADENE I AVDELINGENE MINE (0357, 201.15).
   *
   * En frivillig ser vaktene sine; en avdelingsleder med samme rolle ser i tillegg vaktene hen
   * styrer. To lister som ORES — ikke to regler som krangler om hvem som vinner.
   */
  | {
      readonly kind: 'ownOrDepartments';
      readonly personId: PersonId;
      readonly departmentIds: readonly Uuid[];
    }
  | { readonly kind: 'participant'; readonly personId: PersonId }
  | { readonly kind: 'granted'; readonly objectIds: readonly Uuid[] }
  /**
   * «Bare de festivalene og årgangene hen er lagt til i» (sak 182).
   *
   * TO LISTER OG IKKE ÉN, fordi de to måtene å bli lagt til på peker på hver sin nøkkel:
   * `festivalIds` er «alle årganger av denne festivalen», `editionIds` er de enkeltårgangene
   * `edition_roles` gir. Et repository for festivaler leser den første, et for årganger begge —
   * en årgang er med hvis DEN er nevnt, eller hvis festivalen dens er det.
   *
   * Begge tomme betyr ingen rader. Det er meningen: en rolle uten `festival:access_all` som ikke
   * er lagt til noe sted, ser ingen festivaler. Standarden i matrisen er nettopp derfor `yes` for
   * alle fire rollene — ingen mister noe før noen bevisst skrur av bryteren.
   */
  | {
      readonly kind: 'assigned';
      readonly festivalIds: readonly Uuid[];
      readonly editionIds: readonly EditionId[];
    };

export type DenyReason =
  | 'no_rule_matched'
  | 'wrong_organization'
  /** Raden er delt, men ikke for DENNE handlingen. Se `SHARED_ACTIONS` i `can.ts`. */
  | 'action_not_shared'
  | 'not_a_member'
  | 'role_not_permitted'
  | 'not_creator'
  | 'not_assignee'
  | 'not_participant'
  /**
   * Handlingen finnes bare i et BYRÅHUS (QA14 #3a).
   *
   * Ikke `role_not_permitted`: rollen kan godt ha cella — malen gir `artist_agreement:read` til
   * superadmin, admin og member i hver organisasjon — det er HUSET som ikke har forholdet.
   * Avslaget er derfor en 403 med sin egen grunn og ikke en 404: arrangøren vet utmerket godt at
   * artisten finnes, og å skjule eksistensen ville bare gitt en supporthenvendelse.
   */
  | 'not_an_agency'
  /**
   * Handlingen finnes ikke i et PERSONLIG HUS (sak 216). Huset er der fordi en artist uten
   * organisasjon må stå et sted; personen er superadmin i det, og uten dette steget kunne hen
   * opprettet festivaler og invitert medlemmer i et hus som ikke skal ha organisasjonspreg.
   */
  | 'not_in_a_personal_house'
  | 'not_in_department'
  /** Rollen har ikke `festival:access_all`, og personen er ikke lagt til i denne festivalen (182). */
  | 'not_assigned_festival'
  | 'grant_missing'
  | 'grant_insufficient'
  | 'resource_immutable'
  | 'last_superadmin'
  | 'cannot_grant_higher_role'
  /**
   * Handlingen ville rammet aktøren selv (QA15 blokk B2).
   *
   * Den står her og ikke som en `if` i tjenesten fordi nettleseren stiller det samme spørsmålet:
   * knappen «Deaktiver» skal ikke tegnes på din egen rad i det hele tatt, og det svaret må komme
   * fra den samme funksjonen som avviser kallet (prinsipp 7).
   */
  | 'cannot_target_self'
  /** A non-overridable action attempted by anyone but a superadmin. Not reachable through data. */
  | 'superadmin_only'
  /**
   * Aldri for en gjest, uansett nivå (sak 232, `GUEST_NEVER` i `guest.ts`): økonomi, kontrakt,
   * bud, deling videre, innstillinger og sletting av programpost, avdeling og årgang.
   */
  | 'guest_never'
  /**
   * ORGANISASJONEN HAR IKKE MODULEN (G1, 253.6). Ressursen hører til en modul (`RESOURCE_MODULES`
   * i `modules.ts`) som er av i dette huset. Gjelder lesing og skriving, og bare rader i eget hus:
   * en rad et annet hus har delt, er det huset sin modul.
   */
  | 'module_disabled'
  /**
   * BARE ARRANGØREN (D2, QA13 #110). Motpart og gjest får aldri gå over taket på gjestelista,
   * kvittere ut ankomne, sette akkrediteringsnivå eller se arrangørens økonomi — uansett celle.
   */
  | 'organiser_only'
  /*
   * TOKENS UTENFRA (B3, `external-access.ts`): organisasjonen har slått av AI, eller bare skriving;
   * handlingen er sperret; eller den krever bekreftelse i to steg (revisjonen 02.11).
   */
  | 'ai_access_disabled'
  | 'ai_write_disabled'
  | 'external_blocked'
  | 'ai_confirmation_required'
  | 'external_draft_only';

export type PolicyResult =
  | { readonly allowed: true; readonly scope: QueryScope }
  | { readonly allowed: false; readonly reason: DenyReason };

/** One cell of one organization's matrix, exactly as `role_permissions` stores it (§6.6a). */
export interface PermissionRow {
  readonly role: OrgRole;
  readonly resource: ResourceType;
  readonly action: Verb; // resource + ':' + action === Action
  readonly allowed: boolean;
}

/**
 * One organization's matrix, in memory. Built once per request; every answer after that is a
 * Set lookup, so `can()` stays pure and synchronous and the browser can call the same function.
 */
export interface PermissionSet {
  readonly organizationId: OrganizationId;
  /** TRUE only if a row says so. No row, or allowed = false, is FALSE. Never `undefined`. */
  allows(role: ActorRole, action: Action): boolean;
  /** Everything one role may do - the /v1/me payload and the Phase 2 Settings grid. */
  actionsFor(role: ActorRole): readonly Action[];
}

const permissionRowShape = z.object({
  role: z.enum(ORG_ROLES),
  resource: z.enum(RESOURCE_TYPES),
  action: z.enum(VERBS),
  allowed: z.boolean(),
});

/** Validates `role_permissions` rows arriving from the database or a seed file. */
export const permissionRowSchema: z.ZodType<PermissionRow> = permissionRowShape.meta({
  id: 'PermissionRow',
});

/**
 * Turns the rows of one organization into the object `can()` interrogates.
 *
 * Deny by default is structural here: the set is built **only** from rows whose `allowed` is
 * true, so "no row" and "allowed = false" are literally the same state and there is no `??` or
 * `LEFT JOIN` anywhere that could turn absence into assent.
 *
 * `actionsFor('superadmin')` additionally reports the locked actions (§11.3.1). They are decided
 * in code, never stored as rows — the database refuses to store them — so a matrix-only answer
 * would tell a superadmin's UI that it cannot do things it demonstrably can.
 */
export function buildPermissionSet(
  organizationId: OrganizationId,
  rows: readonly PermissionRow[],
): PermissionSet {
  const byRole = new Map<OrgRole, Set<Action>>();
  for (const row of rows) {
    if (!row.allowed) continue;
    const action = joinAction(row.resource, row.action);
    let set = byRole.get(row.role);
    if (!set) {
      set = new Set<Action>();
      byRole.set(row.role, set);
    }
    set.add(action);
  }
  return makePermissionSet(organizationId, byRole);
}

/**
 * The browser's counterpart to `buildPermissionSet()`: `/v1/me` hands the client the flat
 * `Action[]` for its own role, not the organization's whole matrix, and this turns it back into
 * a `PermissionSet` so the client can build an `Actor` and call the same `can()`.
 */
export function permissionSetFromActions(
  organizationId: OrganizationId,
  role: ActorRole,
  actions: readonly Action[],
): PermissionSet {
  return makePermissionSet(organizationId, new Map([[role, new Set(actions)]]));
}

function makePermissionSet(
  organizationId: OrganizationId,
  byRole: ReadonlyMap<ActorRole, ReadonlySet<Action>>,
): PermissionSet {
  return {
    organizationId,
    allows(role, action) {
      /* En gjest leser aldri matrisen (sak 232): gjestens rettigheter er hvitlista i `guest.ts`. */
      if (role === 'guest') return false;
      return byRole.get(role)?.has(action) === true;
    },
    actionsFor(role) {
      if (role === 'guest') return [];
      const granted = [...(byRole.get(role) ?? [])];
      if (role === 'superadmin') {
        for (const action of LOCKED_ACTIONS.keys()) {
          if (!granted.includes(action)) granted.push(action);
        }
      }
      return granted.sort();
    },
  };
}

/**
 * HAR ORGANISASJONEN MODULEN BYRÅ (ARTISTER)? (sak 239.40; 246.6, regel 4: «Modulene er den eneste
 * regelen».) Modulene avgjør når aktøren har dem, ellers `agencyModule` fra `resolve_actor`. Typen
 * spørres aldri: uten noen av dem er svaret nei, som er den lukkende retningen.
 */
export function hasAgencyModule(actor: Pick<Actor, 'modules' | 'agencyModule'>): boolean {
  return actor.modules === undefined
    ? actor.agencyModule === true
    : actor.modules.has('agency');
}
